/**
 * Bazaarvoice font-weight override
 *
 * BV's Fonts panel offers no numeric font weight — each element type has a Bold checkbox that
 * emits `font-weight: bold` (700) when ticked and `normal` (400) when not. Our design system's
 * bold is 500, so neither value is usable and the correction has to happen theme-side.
 *
 * Most of it can't be done in CSS. BV builds Reviews and Q&A with styled-components, so every
 * class name is a build hash regenerated on their own release schedule, and the semantic tags
 * they do use carry both weights — the widgets render bold and regular `<button>`s that share
 * the tag, so a blanket rule would push the regular ones up too. The one stable signal is the
 * computed weight itself, which only JS can read. Headings are the exception: BV renders those
 * uniformly bold, so scss/components/_bazaarvoice.scss corrects them declaratively and they
 * never reach this walker still bold.
 *
 * Both widgets render into the light DOM while "Allow CSS overrides" is enabled in the BV
 * portal. That is a vendor-side toggle: disabling it puts them back behind a shadow root, where
 * neither this script nor the stylesheet reaches them.
 */
const initBazaarvoiceFontWeight = () => {
  // Modern browsers resolve font-weight to a number, but the CSSOM spec doesn't strictly
  // guarantee it — checking both forms costs nothing and can't miss a bold element either way.
  const BOLD_WEIGHTS = new Set(['700', 'bold']);
  const TARGET_WEIGHT = '500';
  const CONTAINERS = '[data-bv-show="reviews"], [data-bv-show="questions"]';

  const debold = (el) => {
    if (BOLD_WEIGHTS.has(getComputedStyle(el).fontWeight)) {
      // !important: BV styles states that aren't rendered yet (the ask-a-question form, filter
      // and sort menus), so the inline style has to win without knowing what rule it's beating.
      el.style.setProperty('font-weight', TARGET_WEIGHT, 'important');
    }
  };

  const deboldTree = (node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    debold(node);
    node.querySelectorAll('*').forEach(debold);
  };

  // The containers ship in the theme's own markup, so they're present long before BV's loader
  // fills them. Observing from the start catches that first render as an ordinary childList
  // mutation, and React's later re-renders — sorting, filtering, searching, paginating, opening
  // the ask-a-question form — the same way.
  // Re-checks only the mutated node, not the whole container (1000+ nodes for Reviews alone) —
  // a full re-scan on every class/style toggle would compound BV's own main-thread strain.
  const watchContainer = (container) => {
    // None of this reaches BV's markup if "Allow CSS overrides" is turned off in the BV portal:
    // the widget moves behind a shadow root, querySelectorAll stops crossing into it, and the
    // page renders bold with nothing in the console to say why. Attaching a shadow root fires no
    // mutation of its own, so the check re-runs on every wake-up the observer does get —
    // data-bv-ready is in the filter below because BV flips it once the container has rendered.
    let shadowRootReported = false;

    const reportShadowRoot = () => {
      if (shadowRootReported || !container.shadowRoot) return;
      shadowRootReported = true;
      console.warn(
        `bfx-bazaarvoice-font-weight: [data-bv-show="${container.dataset.bvShow}"] rendered into a shadow root, so its font weight is left at Bazaarvoice's default. Re-enable "Allow CSS overrides" in the Bazaarvoice portal.`
      );
    };

    reportShadowRoot();
    deboldTree(container);

    const observer = new MutationObserver((mutations) => {
      reportShadowRoot();

      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          debold(mutation.target);
        } else {
          mutation.addedNodes.forEach(deboldTree);
        }
      });
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'data-bv-ready'],
    });
  };

  document.querySelectorAll(CONTAINERS).forEach(watchContainer);
};

initBazaarvoiceFontWeight();
